import type { User } from 'firebase/auth'
import { onAuthStateChanged, onIdTokenChanged, getIdToken } from 'firebase/auth'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { auth } from '@/frontend/lib/firebase'
import { startLineLogin, handleLineLoginCallback, getLineAuthErrorMessage } from '@/frontend/lib/lineAuth'

interface AuthContextType {
  user: User | null
  loading: boolean
  isSignedIn: boolean
  isLoaded: boolean
  tokenRefreshing: boolean
  getToken: (forceRefresh?: boolean) => Promise<string | null>
  // LINE Login methods
  signInWithLine: (options?: { redirect_uri?: string }) => Promise<void>
  handleLineCallback: (callbackUrl: string) => Promise<void>
  getLineAuthError: (error: any) => string
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Firebase Authentication Provider
 * Provides authentication state throughout the application
 * Replaces Clerk's ClerkProvider functionality
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [tokenRefreshing, setTokenRefreshing] = useState(false)

  useEffect(() => {
    console.log('[Auth] Setting up Firebase auth state listener')
    
    const authStateUnsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('[Auth] Auth state changed:', {
        hasUser: !!user,
        userId: user?.uid,
        email: user?.email,
        timestamp: new Date().toISOString()
      })
      
      setUser(user)
      setLoading(false)
    })

    // Listen for ID token changes (including automatic refresh)
    const tokenUnsubscribe = onIdTokenChanged(auth, (user) => {
      if (user) {
        console.log('[Auth] ID token changed:', {
          userId: user.uid,
          tokenRefreshTime: new Date().toISOString(),
          email: user.email
        })
        
        // Update user state in case token refresh brought new user info
        setUser(user)
      }
    })

    return () => {
      authStateUnsubscribe()
      tokenUnsubscribe()
    }
  }, [])

  const getToken = useCallback(async (forceRefresh = false): Promise<string | null> => {
    try {
      if (!user) {
        console.warn('[Auth] No user available for token retrieval')
        return null
      }

      if (forceRefresh) {
        console.log('[Auth] Force refreshing ID token for user:', user.uid)
        setTokenRefreshing(true)
      }

      const token = await getIdToken(user, forceRefresh)
      
      if (forceRefresh) {
        console.log('[Auth] Token force refresh completed successfully:', {
          userId: user.uid,
          tokenLength: token?.length || 0,
          timestamp: new Date().toISOString()
        })
      }
      
      return token
    } catch (error) {
      console.error('[Auth] Failed to get ID token:', {
        error,
        forceRefresh,
        userId: user?.uid,
        errorCode: (error as any)?.code,
        errorMessage: (error as any)?.message
      })
      
      // Handle specific Firebase auth errors
      if ((error as any)?.code === 'auth/network-request-failed') {
        console.warn('[Auth] Network error during token refresh, will retry on next request')
        if (forceRefresh) {
          toast.error('Network error refreshing session. Please check your connection.')
        }
      } else if (forceRefresh) {
        // Only show error toast for force refresh failures (not automatic background refreshes)
        toast.error('Session refresh failed. You may need to sign in again.')
      }
      
      return null
    } finally {
      if (forceRefresh) {
        setTokenRefreshing(false)
      }
    }
  }, [user])

  // LINE Login methods
  const signInWithLine = async (options?: { redirect_uri?: string }): Promise<void> => {
    try {
      console.log('[Auth] Initiating LINE login')
      await startLineLogin(options)
    } catch (error) {
      console.error('[Auth] LINE login initiation failed:', error)
      throw error
    }
  }

  const handleLineCallback = async (callbackUrl: string): Promise<void> => {
    try {
      console.log('[Auth] Handling LINE callback')
      await handleLineLoginCallback(callbackUrl)
      console.log('[Auth] LINE authentication completed successfully')
    } catch (error) {
      console.error('[Auth] LINE callback handling failed:', error)
      throw error
    }
  }

  const getLineAuthError = (error: any): string => {
    return getLineAuthErrorMessage(error)
  }

  const value: AuthContextType = {
    user,
    loading,
    isSignedIn: !!user,
    isLoaded: !loading,
    tokenRefreshing,
    getToken,
    // LINE Login methods
    signInWithLine,
    handleLineCallback,
    getLineAuthError,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access Firebase authentication state
 * Replaces Clerk's useAuth hook
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Hook to access the current user
 * Replaces Clerk's useUser hook
 */
export function useUser() {
  const { user, loading } = useAuth()
  return {
    user,
    isLoaded: !loading,
  }
}